import React, { useMemo } from 'react';
import { FieldConfig, MappingValue, DropdownOption, SourceType } from './types';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';

/**
 * The two editable cells of one product-mapping row: **Source** and **Value**.
 *
 * Since § 133 this file exports CELLS rather than a `<tr>`. `MappingTable` is a DS
 * `Table` now, and a DS table owns its own rows — a child that renders `<tr>`/`<td>`
 * cannot live inside one. Splitting at the cell boundary is also what the old row
 * component was already doing internally: `renderViewMode` and `renderEditMode` each
 * returned exactly this pair.
 *
 * The value cell's shape still depends on the chosen source, unchanged:
 *
 * | source | control |
 * |---|---|
 * | `Field` | a dropdown of SmartStudio fields |
 * | `Attribute` | a free-text input |
 * | `Fixed Value` | the fixed value, read-only |
 * | *(none)* | a disabled "Select a source first" |
 */

type CellProps = {
    rowId: string;
    fieldConfig: FieldConfig;
    mapping: MappingValue | undefined;
    fieldOptions: DropdownOption[];
    onSourceChange: (fieldKey: string, source: string) => void;
    onValueChange: (fieldKey: string, value: string) => void;
    hasSourceError?: boolean;
    hasValueError?: boolean;
};

/** What the Value cell shows when the row is not being edited. */
export const displayValueFor = (
    fieldConfig: FieldConfig,
    mapping: MappingValue | undefined,
): string => {
    if ((mapping?.source ?? '') === 'Fixed Value') return fieldConfig.fixedValue ?? '';
    return mapping?.value ?? '';
};

/** The SmartStudio field name, plus the "(Optional)" marker. */
export const MappingFieldLabel = ({
    rowId,
    fieldConfig,
}: {
    rowId: string;
    fieldConfig: FieldConfig;
}) => (
    <span title={fieldConfig.label} data-testid={`${rowId}-label`}>
        <Typography variant="body-primary" as="span">
            {fieldConfig.label}
        </Typography>
        {!fieldConfig.isMandatory && (
            <Typography variant="body-primary" as="span" className="tw-ml-1 tw-text-ink-tertiary">
                (Optional)
            </Typography>
        )}
    </span>
);

export const MappingSourceCell = ({
    rowId,
    fieldConfig,
    mapping,
    onSourceChange,
    hasSourceError = false,
}: Omit<CellProps, 'fieldOptions' | 'onValueChange' | 'hasValueError'>) => {
    const sourceOptions = useMemo<DropdownOption[]>(
        () => fieldConfig.allowedSources.map((source) => ({ label: source, value: source })),
        [fieldConfig.allowedSources]
    );

    return (
        /* § 323 — `tw-w-full` belongs on THIS div, not only on the control inside it:
           the cell's flex row makes its element child the flex item, and that child is
           this testid wrapper. Without it the wrapper shrinks to fit and the Dropdown's
           own full width is 100% of a box already collapsed to the label's width. */
        <div className="tw-w-full" data-testid={`${rowId}-source-dropdown-container`}>
            <Dropdown
                /* § 323 — see the note in `FeedVersionsTable`. Without this the trigger is
                   as wide as its own label, so "Attribute" and "Fixed Value" rendered at
                   different widths in one column. */
                className="tw-w-full"
                label=""
                aria-label={`Source for ${fieldConfig.label}`}
                options={sourceOptions}
                value={(mapping?.source ?? '') as SourceType | ''}
                onChange={(value) => onSourceChange(fieldConfig.key, String(value))}
                placeholder="Select source"
                usePortal
                size="sm"
                error={hasSourceError}
                data-testid={`${rowId}-source-select`}
            />
        </div>
    );
};

export const MappingValueCell = ({
    rowId,
    fieldConfig,
    mapping,
    fieldOptions,
    onValueChange,
    hasValueError = false,
}: Omit<CellProps, 'onSourceChange' | 'hasSourceError'>) => {
    const sourceType = (mapping?.source ?? '') as SourceType | '';

    switch (sourceType) {
        case 'Field':
            return (
                <div className="tw-w-full" data-testid={`${rowId}-value-dropdown-container`}>
                    <Dropdown
                        /* § 323 — matches the TextField branches below, which an `<input>`'s
                           intrinsic width happened to make uniform. */
                        className="tw-w-full"
                        label=""
                        aria-label={`Value for ${fieldConfig.label}`}
                        options={fieldOptions}
                        value={mapping?.value ?? ''}
                        onChange={(value) => onValueChange(fieldConfig.key, String(value))}
                        placeholder="Select Field"
                        usePortal
                        size="sm"
                        error={hasValueError}
                        data-testid={`${rowId}-value-select`}
                    />
                </div>
            );

        case 'Attribute':
            return (
                <TextField
                    className="tw-w-full"
                    label=""
                    aria-label={`Value for ${fieldConfig.label}`}
                    value={mapping?.value ?? ''}
                    placeholder="Enter Value"
                    onChange={(value) => onValueChange(fieldConfig.key, value)}
                    error={hasValueError}
                    size="sm"
                    data-testid={`${rowId}-value-input`}
                />
            );

        case 'Fixed Value':
            /* Read-only by design: the value comes from `MAPPING_FIELDS`, not the user.
               `disabled` (not just `readOnly`) is what the legacy input had, and it is
               what keeps it out of the tab order. */
            return (
                <TextField
                    className="tw-w-full"
                    label=""
                    aria-label={`Value for ${fieldConfig.label}`}
                    value={fieldConfig.fixedValue ?? ''}
                    disabled
                    readOnly
                    size="sm"
                    data-testid={`${rowId}-value-display`}
                />
            );

        default:
            return (
                <TextField
                    className="tw-w-full"
                    label=""
                    aria-label={`Value for ${fieldConfig.label}`}
                    value=""
                    placeholder="Select a source first"
                    disabled
                    error={hasValueError}
                    size="sm"
                    data-testid={`${rowId}-value-placeholder`}
                />
            );
    }
};
